import React, { useState, useEffect, useRef } from 'react';
import { Menu, User, LogOut, Crown, LayoutDashboard, Building2, ChevronLeft, ChevronRight, HelpCircle, Zap, X, FileText, Lock, LifeBuoy, Shield, Briefcase, Trophy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';

interface NavbarProps {
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

export function Navbar({ isCollapsed, onCollapse }: NavbarProps) {
  const { profile, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Hide navbar on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        mobileMenuRef.current &&
        mobileButtonRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !mobileButtonRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }

      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    }

    if (isMobileMenuOpen || showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      if (isMobileMenuOpen) {
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.body.style.overflow = '';
      };
    }
  }, [isMobileMenuOpen, showProfileMenu]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowProfileMenu(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMobileMenuOpen(false);
      setShowProfileMenu(false);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const navigationItems = [
    { label: 'Dashboard', Icon: LayoutDashboard, path: '/dashboard', active: location.pathname === '/dashboard' },
    { label: 'Company Profile', Icon: Building2, path: '/profile', active: location.pathname === '/profile' },
    { label: 'Workspace', Icon: Briefcase, path: '/workspace', active: location.pathname.startsWith('/workspace') },
    { label: 'The Winning Bid', Icon: Trophy, path: '/blog', active: location.pathname === '/blog' },
    { label: 'FAQ', Icon: HelpCircle, path: '/faq', active: location.pathname === '/faq' },
    { label: 'Support', Icon: LifeBuoy, path: '/support', active: location.pathname === '/support' },
  ];

  // Add admin link if user has admin role
  if (profile?.role === 'admin') {
    navigationItems.push({
      label: 'Admin',
      Icon: Shield,
      path: '/admin',
      active: location.pathname.startsWith('/admin'),
    });
  }

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setShowProfileMenu(false);
  };

  const companyInitials = (profile?.company_name || 'My Company')
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const Logo = ({ showWord = true }: { showWord?: boolean }) => (
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-7 bg-bw-sidebar-active rounded-lg flex items-center justify-center flex-shrink-0">
        <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
      </div>
      {showWord && <span className="text-[15px] font-bold text-white tracking-tight">BidWin</span>}
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        ref={mobileButtonRef}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="fixed top-4 right-4 z-50 p-2 rounded-lg bg-bw-sidebar text-white shadow-lg md:hidden"
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" />}

      {/* Mobile Menu (NavDrawer) — navy, full nav */}
      <div
        ref={mobileMenuRef}
        className={`fixed inset-y-0 right-0 w-[262px] bg-bw-sidebar shadow-xl z-40 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Header */}
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <Logo />
          </div>

          {/* Mobile Navigation */}
          <div className="flex-1 overflow-y-auto py-3">
            {navigationItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-5 py-3 mx-2 my-0.5 rounded-lg w-[calc(100%-1rem)] ${
                  item.active ? 'bg-bw-sidebar-active text-white font-semibold' : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.Icon className="h-[18px] w-[18px]" />
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Mobile User Section */}
          <div className="border-t border-white/10 px-5 py-4">
            <button
              onClick={() => handleNavigation('/subscription/plans')}
              className="w-full flex items-center justify-between px-3 py-2 bg-bw-sidebar-active text-white rounded-lg hover:brightness-110 mb-4 transition-all"
            >
              <span className="text-sm font-semibold flex items-center gap-2"><Zap className="h-4 w-4" /> Manage Plan</span>
              <span className="text-[9px] font-bold px-1.5 py-0.5 bg-white/15 rounded">PRO</span>
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-bw-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {companyInitials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{profile?.company_name || 'My Profile'}</p>
                <p className="text-xs text-white/50 truncate">{profile?.membership_tier ? `${profile.membership_tier}` : profile?.email}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-3 py-2 text-white/70 hover:bg-white/10 rounded-lg"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Sign Out
            </button>
          </div>

          {/* Mobile Footer Links */}
          <div className="border-t border-white/10 px-5 py-3">
            <div className="flex justify-between">
              <Link to="/terms" className="text-xs text-white/50 hover:text-white flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
                <FileText className="h-3.5 w-3.5 mr-1.5" /> Terms
              </Link>
              <Link to="/privacy" className="text-xs text-white/50 hover:text-white flex items-center" onClick={() => setIsMobileMenuOpen(false)}>
                <Lock className="h-3.5 w-3.5 mr-1.5" /> Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar — ink navy, collapsible */}
      <aside
        className={`hidden md:flex flex-col fixed top-0 left-0 h-screen bg-bw-sidebar z-40 transition-all duration-200 ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo + collapse toggle */}
        <div className={`h-14 flex items-center border-b border-white/10 flex-shrink-0 ${isCollapsed ? 'justify-center px-0' : 'justify-between px-4'}`}>
          {isCollapsed ? (
            <Logo showWord={false} />
          ) : (
            <>
              <Logo />
              <button
                onClick={() => onCollapse(true)}
                className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10"
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </>
          )}
        </div>

        {isCollapsed && (
          <div className="flex justify-center py-2 border-b border-white/10">
            <button
              onClick={() => onCollapse(false)}
              className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10"
              title="Expand sidebar"
              aria-label="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {navigationItems.map((item) => (
            <div key={item.label} className="px-2 mb-0.5">
              <button
                onClick={() => handleNavigation(item.path)}
                title={item.label}
                className={`w-full flex items-center rounded-lg transition-colors ${
                  isCollapsed ? 'justify-center px-0 py-2.5' : 'gap-2.5 px-3 py-2'
                } ${
                  item.active
                    ? 'bg-bw-sidebar-active text-white font-semibold'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.Icon className={isCollapsed ? 'h-[18px] w-[18px]' : 'h-[15px] w-[15px]'} />
                {!isCollapsed && <span className="text-[13px]">{item.label}</span>}
              </button>
            </div>
          ))}
        </nav>

        {/* Footer — plan + account */}
        <div className={`border-t border-white/10 flex-shrink-0 ${isCollapsed ? 'py-3' : 'px-4 py-3'}`}>
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-2.5">
              <button
                onClick={() => handleNavigation('/subscription/plans')}
                className="w-8 h-8 bg-bw-sidebar-active rounded-lg flex items-center justify-center"
                title="Manage Plan"
                aria-label="Manage Plan"
              >
                <Zap className="h-4 w-4 text-white" />
              </button>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="w-7 h-7 rounded-full bg-bw-primary flex items-center justify-center text-white text-[10px] font-bold"
                title={profile?.company_name || 'Account'}
              >
                {companyInitials}
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => handleNavigation('/subscription/plans')}
                className="w-full flex items-center justify-between px-2.5 py-1.5 bg-bw-sidebar-active rounded-md mb-2.5 hover:brightness-110 transition-all"
              >
                <span className="text-[11px] font-semibold text-white flex items-center gap-1.5"><Zap className="h-3 w-3" /> Manage Plan</span>
                <span className="text-[9px] font-bold text-white/80 px-1.5 py-0.5 bg-white/15 rounded capitalize">
                  {profile?.membership_tier && profile.membership_tier !== 'free' ? 'PRO' : 'Free'}
                </span>
              </button>
              <div
                className="flex items-center gap-2 cursor-pointer rounded-md p-1 -m-1 hover:bg-white/5"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <div className="min-w-[26px] w-[26px] h-[26px] rounded-full bg-bw-primary flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                  {companyInitials}
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-white truncate max-w-[120px]">{profile?.company_name || 'My Profile'}</p>
                  <p className="text-[10px] text-white/50 truncate capitalize">{profile?.membership_tier || 'Free'}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Profile Menu (sign out) */}
      {showProfileMenu && (
        <div
          ref={profileMenuRef}
          className="hidden md:block fixed left-2 bottom-16 w-56 bg-bw-surface rounded-lg shadow-card border border-bw-border z-50"
        >
          <div className="p-3 border-b border-bw-border">
            <p className="font-semibold text-sm text-bw-ink truncate flex items-center gap-2">
              <User className="h-4 w-4 text-bw-muted" /> {profile?.company_name || 'My Profile'}
            </p>
            <p className="text-xs text-bw-muted truncate mt-0.5">{profile?.email}</p>
          </div>
          <div className="p-1.5">
            <button
              onClick={() => handleNavigation('/subscription/plans')}
              className="w-full px-3 py-2 text-left text-sm text-bw-ink hover:bg-bw-surface-subtle rounded-md flex items-center gap-2"
            >
              <Crown className="h-4 w-4 text-bw-primary" /> Manage Plan
            </button>
            <button
              onClick={handleSignOut}
              className="w-full px-3 py-2 text-left text-sm text-urgent hover:bg-urgent-bg rounded-md flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      )}
    </>
  );
}
